import { get, post, remove, put, upload } from "./api";
import {
  Project,
  WorkspaceDto,
  CreateWorkspaceForm,
  Field,
  BucketFormEdit,
  BucketFormCreate,
  Task,
  TaskForm,
  ProjectSetting,
} from "./NetTypes";

export const getProjects = async (): Promise<WorkspaceDto[]> => {
  const response = await get({ path: `/workspaces` });
  return response.data;
};

export const getMembers = async (projectId: string): Promise<ProjectSetting[]> => {
  const response = await get({ path: `/workspaces/${projectId}/members` });
  return response.data;
};

export const getProject = async (projectId: string): Promise<any> => {
  const response = await get({ path: `/workspaces/${projectId}` });
  return response.data;
};

export const parseEmailSignature = async (
  projectId: string,
  signature: string,
  format: "html"
): Promise<any> => {
  const response = await post({
    path: `/workspaces/${projectId}/emails/signature/parse`,
    data: { signature, format },
  });
  return response.data;
};

export const extractEmailSignature = async (
  projectId: string,
  emailBody: string,
  format: "html" | "plain" = "plain"
): Promise<any> => {
  const response = await post({
    path: `/workspaces/${projectId}/emails/signature/extract`,
    data: { emailBody, format },
  });
  return response.data;
};

export interface WorkspaceAutomationSettings {
  enabled: boolean;
  maxExecutionsPerMonth: number | null;
  blockWhenExceeded: boolean;
}

export interface WorkspaceAutomationUsageDto {
  workspaceId: string;
  periodKey: string;
  executedCount: number;
  blockedCount: number;
  limit: number | null;
  remaining: number | null;
  blocked: boolean;
  setting: WorkspaceAutomationSettings;
}

export const getWorkspaceAutomationUsage = async (
  projectId: string,
  period?: string
): Promise<WorkspaceAutomationUsageDto> => {
  const response = await get({
    path: `/workspaces/${projectId}/automation-usage`,
    params: period ? { period } : undefined,
  });
  return response.data;
};

export const deleteProject = async (projectId: string): Promise<any> => {
  const response = await remove({ path: `/workspaces/${projectId}` });
  return response.data;
};

export const createProject = async (project: CreateWorkspaceForm): Promise<Project> => {
  const response = await post({ path: `/workspaces`, data: project });
  return response.data;
};

/** Pipeline wizard: start async workspace generation. Returns jobId for polling. */
export interface WizardStartForm {
  workspaceType: string;
  description?: string;
  name?: string;
  currency?: string;
  projects?: { value: string; id: string; label: string; data?: Record<string, unknown> }[];
  /** When false, skip creating sample deal. Default true. */
  generateSampleData?: boolean;
  stageNames: string[];
  saleColumnIds: string[];
  savedFilterIds: string[];
  automationTemplateIds: string[];
}

export const startWizard = async (form: WizardStartForm): Promise<{ jobId: string }> => {
  const response = await post({ path: `/workspaces/wizard/start`, data: form });
  return response.data;
};

/** Pipeline wizard: poll generation status until phase is DONE or FAILED. */
export interface WizardStatusResponse {
  phase: string;
  workspaceId?: string;
  error?: string;
}

export const getWizardStatus = async (jobId: string): Promise<WizardStatusResponse> => {
  const response = await get({ path: `/workspaces/wizard/status/${jobId}` });
  return response.data;
};

export const getWizardStages = async (): Promise<any> => {
  const response = await get({ path: `/workspaces/wizard/stages` });
  return response.data;
};

export const getWizardStagesByType = async (type: string): Promise<string[]> => {
  const response = await get({ path: `/workspaces/wizard/stages/${type}` });
  return response.data;
};

export const getWizardSavedFilters = async (): Promise<any> => {
  const response = await get({ path: `/workspaces/wizard/saved-filters` });
  return response.data;
};

export const getWizardSavedFiltersByType = async (type: string): Promise<any[]> => {
  const response = await get({ path: `/workspaces/wizard/saved-filters/${type}` });
  return response.data;
};

export const getWizardSaleColumns = async (): Promise<any> => {
  const response = await get({ path: `/workspaces/wizard/sale-columns` });
  return response.data;
};

export const getWizardSaleColumnsByType = async (type: string): Promise<string[]> => {
  const response = await get({ path: `/workspaces/wizard/sale-columns/${type}` });
  return response.data;
};

export const getWizardAutomations = async (): Promise<any> => {
  const response = await get({ path: `/workspaces/wizard/automations` });
  return response.data;
};

export const getWizardAutomationsByType = async (type: string): Promise<any[]> => {
  const response = await get({ path: `/workspaces/wizard/automations/${type}` });
  return response.data;
};

/**
 * One-click demo import: uses backend upload + startImport (ImportDataResource).
 * Returns { importId }; the client should poll getImportCSVData(importId) until status is "done" or "failed".
 */
export const runDemoImport = async (
  projectId: string,
  csvKey: string
): Promise<{ importId: string }> => {
  const response = await post({
    path: `/workspaces/${projectId}/demo-import`,
    data: { csvKey },
  });
  return response.data;
};

/**
 * Create automation rules from demo template suggestions (RuleResource.createDemoAutomations).
 * Uses existing RuleService.saveRule on the backend so rules appear in Automations.
 */
export const runDemoAutomations = async (
  projectId: string,
  csvKey: string,
  suggestionIndices: number[]
): Promise<{ created: number; ruleIds: string[] }> => {
  const response = await post({
    path: `/workspaces/${projectId}/demo-automations`,
    data: { csvKey, suggestionIndices },
  });
  return response.data;
};

export const updateProject = async (project: any): Promise<Project> => {
  const response = await put({
    path: `/workspaces/${project.id}`,
    data: project,
  });
  return response.data;
};

export const inviteMembers = async (projectId: string, form: any): Promise<Project> => {
  const response = await put({
    path: `/workspaces/${projectId}/invite-members`,
    data: form,
  });
  return response.data;
};

export const getBuckets = async (projectId: string, type: string): Promise<any> => {
  const response = await get({
    path: `/workspaces/${projectId}/buckets?type=${type}`,
  });
  return { buckets: response.data, type, projectId };
};

export const getFields = async (projectId: string): Promise<Field[]> => {
  const response = await get({ path: `/workspaces/${projectId}/fields` });
  return response.data;
};

export const saveField = (projectId: string, field: any): Promise<any> => {
  return post({ path: `/workspaces/${projectId}/fields`, data: field });
};

export const deleteField = async (projectId: string, fieldId: string): Promise<Field> => {
  const response = await remove({
    path: `/workspaces/${projectId}/fields/${fieldId}`,
  });
  return response.data;
};

export const getFavoriteTree = async (projectId: string): Promise<any[]> => {
  const response = await get({ path: `/workspaces/${projectId}/favorite-tree` });
  return response.data;
};

export const saveFavoriteTree = (projectId: string, favoriteTree: any): Promise<any> => {
  return put({ path: `/workspaces/${projectId}/favorite-tree`, data: favoriteTree });
};

export const getFormSetting = async (projectId: string, type: string): Promise<any> => {
  const response = await get({ path: `/workspaces/${projectId}/form-settings/${type}` });
  return { formSetting: response.data, contentType: type };
};

export const saveFormSetting = (
  projectId: string,
  type: string,
  formSetting: any
): Promise<any> => {
  return put({ path: `/workspaces/${projectId}/form-settings/${type}`, data: formSetting });
};

export const search = async ({
  projectId = "all",
  page = 0,
  pageSize = 50,
  filters,
  view = "GRID",
  types = ["SALE"],
  fields = [],
  sorts = [],
}: any): Promise<any> => {
  const data = {
    projectId,
    page,
    pageSize,
    filters,
    view,
    types,
    fields,
    sorts,
  };
  const response = await post({
    path: `/search`,
    data,
  });
  return Promise.resolve(response.data);
};

export const saveBucket = async (bucket: BucketFormCreate | BucketFormEdit): Promise<any> => {
  if (bucket.setUp) {
    return Promise.resolve({
      id: Math.random().toString(36).substring(7),
      ...bucket,
    });
  }
  // Use PUT for update (existing bucket with id), POST for create (new bucket)
  const isUpdate = "id" in bucket && bucket.id;
  const response = isUpdate
    ? await put({ path: `/buckets`, data: bucket })
    : await post({ path: `/buckets`, data: bucket });
  return response.data;
};

export const deleteBucket = async (
  bucketId: string,
  type: string,
  setUp?: boolean
): Promise<any> => {
  if (setUp) {
    return Promise.resolve({ bucketId, type, setUp });
  }
  await remove({ path: `/buckets/${bucketId}` });

  return Promise.resolve({ bucketId, type, setUp });
};

export const changeBucketOrder = async ({
  projectId,
  bucketId,
  direction,
  type,
  name,
  setUp,
}): Promise<any> => {
  if (setUp) {
    return Promise.resolve({
      projectId,
      bucketId,
      direction,
      type,
      name,
      setUp,
    });
  } else {
    await put({
      path: `/${projectId}/buckets/${bucketId}/${direction}?type=${type}`,
    });
    return Promise.resolve({
      projectId,
      bucketId,
      direction,
      type,
      name,
      setUp,
    });
  }
};

export const createItem = async (task: TaskForm): Promise<Task> => {
  const response = await post({ path: `/items`, data: task });
  return response.data;
};

export const updateItem = async (projectId: string, itemId: string, data: any): Promise<any> => {
  const response = await put({
    path: `/items`,
    data: {
      projectId,
      itemId,
      data,
    },
  });
  return response.data;
};

export const getItem = async (taskId: string): Promise<Task> => {
  const response = await get({ path: `/items/${taskId}` });
  return response.data;
};

export const searchActivities = async ({
  itemId,
  scope,
  pageSize = 50,
  status = "",
  sortBy = "event.createdAt",
  sortDirection = "DESC",
  eventActions = "",
  days = "",
  users = "",
  includeDependencies = false,
}: any): Promise<any> => {
  const depQuery = includeDependencies ? "&includeDependencies=true" : "";
  const response = await get({
    path: `/items/${itemId}/search-activities?scope=${scope}&pageSize=${pageSize}&status=${status}&sortBy=${sortBy}&sortDirection=${sortDirection}&eventActions=${eventActions}&days=${days}&users=${users}${depQuery}`,
  });
  return response.data;
};

export const countActivities = async ({
  itemId,
  scope,
  status = "",
  eventActions = "",
  days = "",
  users = "",
  includeDependencies = false,
}: any): Promise<any> => {
  const depQuery = includeDependencies ? "&includeDependencies=true" : "";
  const response = await get({
    path: `/items/${itemId}/count-activities?scope=${scope}&status=${status}&eventActions=${eventActions}&days=${days}&users=${users}${depQuery}`,
  });
  return response.data;
};

export const getItemLog = async (itemId, logId): Promise<any> => {
  const response = await get({
    path: `/items/${itemId}/logs/${logId}`,
  });
  return response.data;
};

export const deleteItemLog = async (itemId: string, logId: string): Promise<any> => {
  return remove({ path: `/items/${itemId}/logs/${logId}` });
};

export const createItemLogActivity = async (itemId: string, data): Promise<any> => {
  try {
    const response = await post({
      path: `/items/${itemId}/activities/${data.action}`,
      data,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createBulkActivity = async (data): Promise<any> => {
  try {
    const response = await post({
      path: `/items/activities/${data.action}`,
      data,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const markAsDoneActivity = async (
  taskId: string,
  activityId: string,
  status
): Promise<any> => {
  return put({
    path: `/items/${taskId}/activities/${activityId}/mark-as-done/${status}`,
  });
};

export const duplicateItemActivity = async (taskId: string, logId: string): Promise<any> => {
  return put({
    path: `/items/${taskId}/logs/${logId}/duplicate`,
  });
};

export const uploadFileLogActivity = async (
  taskId: string,
  file: any,
  visibility: "Private" | "Shared",
  meta?: { width?: number; height?: number; source?: "User" | "Canva" }
): Promise<any> => {
  const uploadUrl = `/files/uploadFile`;
  const data = new FormData();
  data.append("file", file);
  data.append("visibility", visibility);
  data.append("itemId", taskId);
  if (meta?.width) data.append("width", String(Math.round(meta.width)));
  if (meta?.height) data.append("height", String(Math.round(meta.height)));
  if (meta?.source) data.append("source", meta.source);
  const response = await upload({ path: uploadUrl, data });
  return response.data;
};

export const downloadFileLogActivity = async (fileName: string): Promise<any> => {
  const downloadUrl = `/files/downloadFile/${fileName}`;
  const response = await get({ path: downloadUrl });
  return response.data;
};

export const deleteFileLogActivity = async (activityId: string): Promise<any> => {
  const response = await remove({
    path: `/files/activityFile/${activityId}`,
  });
  return response.data;
};

export const getActivityFileMetrics = async (activityId: string): Promise<any> => {
  const response = await get({
    path: `/files/activityFile/${activityId}/metrics`,
  });
  return response.data;
};

export type MediaInsightsDateUnit = "daily" | "weekly" | "monthly" | "quarterly";

export type MediaInsightsBucket = {
  date: string;
  postCount: number;
  publishedCount: number;
  totalLikeCount: number;
  totalCommentCount: number;
  totalShareCount: number;
  totalViewCount: number;
};

export type MediaInsightsResponse = {
  dateUnit: MediaInsightsDateUnit;
  from: string;
  to: string;
  buckets: MediaInsightsBucket[];
  updatedAt: string;
};

export const getMediaInsights = async (params: {
  dateUnit: MediaInsightsDateUnit;
  from: string;
  to: string;
}): Promise<MediaInsightsResponse> => {
  const search = new URLSearchParams({
    dateUnit: params.dateUnit,
    from: params.from,
    to: params.to,
  });
  const response = await get({
    path: `/files/media-insights?${search.toString()}`,
  });
  return response.data;
};

export const getActivityFileExists = async (fileName: string): Promise<any> => {
  const response = await get({
    path: `/files/activityFile/exists?fileName=${fileName}`,
  });
  return response.data;
};

export const searchFiles = async ({
  projectId = "all",
  page = 0,
  pageSize = 50,
  filters,
  view = "GRID",
  types = ["SALE"],
  fields = [],
  sorts = [],
}: any): Promise<any> => {
  const data = {
    projectId,
    page,
    pageSize,
    filters,
    view,
    types,
    fields,
    sorts,
  };
  const response = await post({
    path: `/files/search`,
    data,
  });
  return Promise.resolve(response.data);
};

export const updateTaskField = async (
  itemId: string,
  fieldId: string,
  fieldValue: any,
  itemType?: string,
  view?: string
): Promise<any> => {
  const data = {
    value: fieldValue,
  };
  let response = { data: {} };
  if (itemType == "ACTIVITY") {
    response = await put({
      path: `/activities/${itemId}/${fieldId}/update-field`,
      data,
    });
  } else {
    response = await put({
      path: `/items/${itemId}/${fieldId}/update-field`,
      data,
    });
  }
  return {
    taskId: itemId,
    fieldId,
    fieldValue,
    itemType,
    view,
    item: response.data,
  };
};

export const updateActivity = async (itemId: string, data: any): Promise<any> => {
  const response = await put({
    path: `/activities/${itemId}`,
    data,
  });
  return response.data;
};

export const updateItemLogo = async (itemId: string, logoBase64String: string) => {
  await put({
    path: `/items/${itemId}/iconUrl/update-field`,
    data: {
      value: logoBase64String,
      type: "BASE64",
    },
  });
};

export const deleteTask = async (taskId: string): Promise<any> => {
  const response = await remove({ path: `/items/${taskId}` });
  return response.data;
};

export const bulkDeleteItems = async ({ itemIds, type }): Promise<any> => {
  const response = await remove({
    path: `/items/bulk-delete`,
    data: { itemIds, type },
  });
  return response.data;
};

/** Refresh interaction statistics (totalTasks, totalComments, totalEmails, etc.) for a single item */
export const refreshInteractionStatistics = async (itemId: string): Promise<any> => {
  const response = await post({ path: `/items/${itemId}/interactions/refresh` });
  return response.data;
};

export async function searchItems(projectId: string, query = "", type: string) {
  const { data = [] } = await get({
    path: `/workspaces/${projectId}/items/search?query=${query}&type=${type}`,
  });
  return Promise.resolve(data);
}

export async function searchIssues(query = "", currentJQL) {
  const { data = {} } = await get({
    path: `/jira/issues?query=${query}&currentJQL=${currentJQL}`,
  });
  return Promise.resolve(data.sections);
}
